import { Switch } from '@mui/material';
import _ from 'lodash';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { usePipedriveStore } from '../..';
import { DealStatus } from '../../interfaces/IDeal';
import { IDealFilterSettings } from '../../interfaces/IFilterSettings';
import { dealStatusToString } from '../../utils/enumUtils';
import { formatDateForInput, parseDate } from '../../utils/utils';
import styles from './GlobalFilters.module.css';

function GlobalFiltersDealsTab({ dealFiltersState, setDealFiltersState }: IGlobalFiltersDealsTabProps) {
    const pipedriveStore = usePipedriveStore();
    const dealStages = useMemo(() => computed(() => _.orderBy(pipedriveStore.dealStages, s => s.order, "asc")), []).get();
    const labelOptions = useMemo(() => computed(() => pipedriveStore.labelOptions), []).get();

    return (
        <div className={styles.columns}>
            <div className={styles.column}>
                <label className={styles.label}>
                    VALUE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={dealFiltersState.minValue || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            minValue: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={dealFiltersState.maxValue || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            maxValue: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    BUILDING SIZE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={dealFiltersState.minBuildingSize || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            minBuildingSize: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={dealFiltersState.maxBuildingSize || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            maxBuildingSize: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    LAND SIZE
                    <div>
                        <input type="number" className={styles.numberInput} placeholder="Enter min" value={dealFiltersState.minLandSize || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            minLandSize: e.target.valueAsNumber || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="number" className={styles.numberInput} placeholder="Enter max" value={dealFiltersState.maxLandSize || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            maxLandSize: e.target.valueAsNumber || null,
                        })} />
                    </div>
                </label>
                <label className={styles.label}>
                    PERSON
                    <input className={styles.textInput} value={dealFiltersState.pipedrivePersonName} onChange={e => setDealFiltersState({
                        ...dealFiltersState,
                        pipedrivePersonName: e.target.value,
                    })} />
                </label>
                <label className={styles.label}>
                    KEYWORD
                    <input className={styles.textInput} value={dealFiltersState.keyword} onChange={e => setDealFiltersState({
                        ...dealFiltersState,
                        keyword: e.target.value,
                    })} />
                </label>
            </div>
            <div className={styles.column}>
                <label className={styles.label}>
                    STATUS
                    <div className={styles.tags}>
                        {[DealStatus.Lost, DealStatus.Open, DealStatus.Won].map(s => (
                            <div
                                key={s}
                                className={dealFiltersState.statuses.includes(s) ? styles.selectedTag : styles.tag}
                                onClick={() => setDealFiltersState({
                                    ...dealFiltersState,
                                    statuses: !dealFiltersState.statuses.includes(s) ? [
                                        ...dealFiltersState.statuses,
                                        s,
                                    ] : dealFiltersState.statuses.filter(st => st !== s),
                                })}
                            >
                                {dealStatusToString(s)}
                            </div>
                        ))}
                    </div>
                </label>
                <label className={styles.label}>
                    STAGES
                    <div className={styles.tags}>
                        {dealStages.map(s => (
                            <div
                                key={s.id}
                                className={dealFiltersState.dealStageIds.includes(s.id) ? styles.selectedTag : styles.tag}
                                onClick={() => setDealFiltersState({
                                    ...dealFiltersState,
                                    dealStageIds: !dealFiltersState.dealStageIds.includes(s.id) ? [
                                        ...dealFiltersState.dealStageIds,
                                        s.id,
                                    ] : dealFiltersState.dealStageIds.filter(id => id !== s.id),
                                })}
                            >
                                {s.name}
                            </div>
                        ))}
                    </div>
                </label>
                <label className={styles.label}>
                    LABELS
                    <div className={styles.tags}>
                        {labelOptions.map(o => (
                            <div
                                key={o.id}
                                className={dealFiltersState.labelIds.includes(o.id) ? styles.selectedTag : styles.tag}
                                onClick={() => setDealFiltersState({
                                    ...dealFiltersState,
                                    labelIds: !dealFiltersState.labelIds.includes(o.id) ? [
                                        ...dealFiltersState.labelIds,
                                        o.id,
                                    ] : dealFiltersState.labelIds.filter(id => id !== o.id),
                                })}
                            >
                                {o.label}
                            </div>
                        ))}
                    </div>
                </label>
                <div className={styles.switchesSection}>
                    DISPLAY OPTIONS
                    <div>
                        <label className={styles.switchRow}>
                            <span className={styles.switchLabel}>Hide Sold Deals</span>
                            <Switch
                                checked={dealFiltersState.hideSold}
                                onChange={e => setDealFiltersState({
                                    ...dealFiltersState,
                                    hideSold: e.target.checked,
                                })}
                            />
                        </label>
                        <label className={styles.switchRow}>
                            <span className={styles.switchLabel}>Hide Non-Fund Deals</span>
                            <Switch
                                checked={dealFiltersState.hideNonFund}
                                onChange={e => setDealFiltersState({
                                    ...dealFiltersState,
                                    hideNonFund: e.target.checked,
                                })}
                            />
                        </label>
                    </div>
                </div>
                <label className={styles.label}>
                    DATE MODIFIED
                    <div>
                        <input type="date" className={styles.numberInput} value={formatDateForInput(dealFiltersState.minDateModified || undefined)} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            minDateModified: parseDate(e.target.value) || null,
                        })} />
                        &nbsp;-&nbsp;
                        <input type="date" className={styles.numberInput} value={formatDateForInput(dealFiltersState.maxDateModified || undefined) || ""} onChange={e => setDealFiltersState({
                            ...dealFiltersState,
                            maxDateModified: parseDate(e.target.value) || null,
                        })} />
                    </div>
                </label>
            </div>
        </div>
    );
};

export default observer(GlobalFiltersDealsTab);

interface IGlobalFiltersDealsTabProps {
    dealFiltersState: IDealFilterSettings;
    setDealFiltersState: (value: IDealFilterSettings) => void;
}